import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Radio,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  PlusOutlined,
  StarFilled,
  SyncOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation } from 'react-apollo';
import { usePlaidLink } from 'react-plaid-link';
import { ADMIN_TREASURY_BANK_LINK_TOKEN } from '../../pages/apollo_queries';
import {
  ADMIN_SET_TREASURY_PREFERRED_BANK,
  ADMIN_TREASURY_DEACTIVATE_BANK,
  ADMIN_TREASURY_LINK_BANK,
  ADMIN_TREASURY_REPAIR_BANK,
} from '../../pages/apollo_mutations';
import { backendMessage } from '../investment-rules/shared';
import { TableMainLabel, TableSubLabel } from '../styled';
import { newAttemptId } from './MovementModal';

// The house's banks under the treasury handle (2026-10-29): what the legacy Manage Sila page did with
// Plaid (`linkAccount`), now where the money moves. The list arrives ALREADY filtered to the usable
// banks and ordered by the backend (preferred first, then by name): nothing is filtered or sorted here.
// The bank actions ask for no reason (TL, 2026-10-29): the audited reason stays on the money movements.
// Every write runs behind a synchronous latch against the double click.

const last4 = (bank) =>
  bank.accountNumber ? `••••${`${bank.accountNumber}`.slice(-4)}` : null;

// What the operator calls the bank: the account name given when linking it, else Sila's own name.
const bankName = (bank) => bank.label || bank.accountName;

const plaidAccountLabel = (account) =>
  [
    account.name || account.official_name || 'Account',
    account.mask ? `••••${account.mask}` : null,
    account.subtype || null,
  ]
    .filter(Boolean)
    .join(' · ');

// Mounted only while a Link session is wanted. `usePlaidLink` builds ONE Plaid instance per token and
// captures the callbacks at that moment, so they go through refs; `ready` lands once the script is
// loaded AND the token is set — `open()` before that is a no-op, hence the effect.
function PlaidSession({ token, onSuccess, onExit }) {
  const successRef = useRef(onSuccess);
  const exitRef = useRef(onExit);
  successRef.current = onSuccess;
  exitRef.current = onExit;
  const stableSuccess = useCallback(
    (publicToken, metadata) => successRef.current(publicToken, metadata),
    []
  );
  const stableExit = useCallback(
    (err, metadata) => exitRef.current(err, metadata),
    []
  );
  const { open, ready, error } = usePlaidLink({
    token,
    onSuccess: stableSuccess,
    onExit: stableExit,
  });
  const openedRef = useRef(false);

  useEffect(() => {
    if (ready && !openedRef.current) {
      openedRef.current = true;
      open();
    }
  }, [ready, open]);

  // The Plaid script itself failed to load (network, blocker): the session cannot start.
  useEffect(() => {
    if (error) exitRef.current(error);
  }, [error]);

  return null;
}

// "Set as preferred" and "Retire": a summary of the bank and one confirmed write.
const BANK_ACTIONS = {
  prefer: {
    title: 'Set as preferred bank',
    okText: 'Set as preferred',
    okType: 'primary',
    mutation: ADMIN_SET_TREASURY_PREFERRED_BANK,
    field: 'adminSetTreasuryPreferredBank',
    description: (bank) =>
      `${bankName(
        bank
      )} will be listed first and proposed by default when money moves.`,
    success: (bank) => `${bankName(bank)} is now the preferred bank`,
  },
  retire: {
    title: 'Retire bank',
    okText: 'Retire',
    okType: 'danger',
    mutation: ADMIN_TREASURY_DEACTIVATE_BANK,
    field: 'adminTreasuryDeactivateBank',
    description: (bank) =>
      `${bankName(
        bank
      )} is retired at Sila and leaves this list; it is not deleted and its past movements stay. Money cannot move through it any more.`,
    success: (bank) => `${bankName(bank)} retired`,
  },
};

function BankActionModal({ type, bank, onClose, onDone }) {
  const spec = BANK_ACTIONS[type];
  const [error, setError] = useState(null);
  const [run, { loading }] = useMutation(spec.mutation);
  // The input carries no attempt id (the backend answers the refreshed list, nothing is replayed), so the
  // guard against the double click is the synchronous latch plus `loading` on the button.
  const inFlightRef = useRef(false);

  const submit = async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setError(null);
    try {
      await run({
        variables: { data: { accountName: bank.accountName } },
      });
      message.success(spec.success(bank));
      onClose();
      onDone();
    } catch (e) {
      setError(backendMessage(e));
    } finally {
      inFlightRef.current = false;
    }
  };

  return (
    <Modal
      visible
      title={spec.title}
      okText={spec.okText}
      okType={spec.okType}
      okButtonProps={{ loading, danger: spec.okType === 'danger' }}
      cancelButtonProps={{ disabled: loading }}
      onOk={submit}
      onCancel={onClose}
      closable={!loading}
      maskClosable={false}
      destroyOnClose
    >
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 16 }}
        />
      )}
      <Typography.Paragraph>
        <Typography.Text strong>{bankName(bank)}</Typography.Text>
        {bank.providerName || last4(bank) ? (
          <Typography.Text type="secondary">
            {' '}
            · {[bank.providerName, last4(bank)].filter(Boolean).join(' ')}
          </Typography.Text>
        ) : null}
      </Typography.Paragraph>
      <Typography.Paragraph type="secondary">
        {spec.description(bank)}
      </Typography.Paragraph>
    </Modal>
  );
}

// Link a NEW bank: the account name first (the operator commits before Plaid opens), then the Link token is
// read and Plaid opens on top; when Plaid returns several accounts the operator picks one; then the
// backend links it under the treasury handle.
function LinkBankModal({ onClose, onDone }) {
  const client = useApolloClient();
  const [form] = Form.useForm();
  const [error, setError] = useState(null);
  const [preparing, setPreparing] = useState(false);
  // { token, accountName, attemptId } while Plaid is open; null while the form is edited.
  const [session, setSession] = useState(null);
  // { publicToken, accounts } when Plaid returned MORE than one account and the operator must pick.
  const [picking, setPicking] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [link, { loading: linking }] = useMutation(ADMIN_TREASURY_LINK_BANK);
  const attemptRef = useRef(null);
  const inFlightRef = useRef(false);

  const prepare = async (values) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setError(null);
    setPreparing(true);
    try {
      const { data } = await client.query({
        query: ADMIN_TREASURY_BANK_LINK_TOKEN,
        fetchPolicy: 'network-only',
      });
      attemptRef.current = newAttemptId();
      setSession({
        token: data.adminTreasuryBankLinkToken,
        accountName: values.accountName.trim(),
        attemptId: attemptRef.current,
      });
    } catch (e) {
      setError(backendMessage(e));
    } finally {
      setPreparing(false);
      inFlightRef.current = false;
    }
  };

  const submitLink = async (publicToken, accountId) => {
    if (inFlightRef.current || !session) return;
    inFlightRef.current = true;
    setError(null);
    try {
      await link({
        variables: {
          data: {
            publicToken,
            selectedAccountId: accountId,
            accountName: session.accountName,
          },
        },
      });
      message.success(
        `${session.accountName} linked to the treasury · frozen at Sila for 5–15 minutes before it can move money`,
        6
      );
      attemptRef.current = null;
      onClose();
      onDone();
    } catch (e) {
      setError(backendMessage(e));
      // Back to the form: a new Link session (new token) is needed to retry.
      setSession(null);
      setPicking(null);
    } finally {
      inFlightRef.current = false;
    }
  };

  const onPlaidSuccess = (publicToken, metadata) => {
    // A callback from a session the operator already left is ignored.
    if (!session || session.attemptId !== attemptRef.current) return;
    const accounts = (metadata && metadata.accounts) || [];
    if (accounts.length === 0) {
      setError('Plaid returned no account to link. Try again.');
      setSession(null);
      return;
    }
    if (accounts.length === 1) {
      submitLink(publicToken, accounts[0].id);
      return;
    }
    setSelectedAccountId(accounts[0].id);
    setPicking({ publicToken, accounts });
  };

  const onPlaidExit = (err) => {
    if (err) {
      setError(
        err.display_message ||
          err.error_message ||
          err.message ||
          'Plaid closed with an error'
      );
    }
    attemptRef.current = null;
    setSession(null);
  };

  const plaidOpen = !!session && !picking;
  const okProps = picking
    ? {
        okText: 'Link this account',
        okButtonProps: { loading: linking, disabled: !selectedAccountId },
        onOk: () => submitLink(picking.publicToken, selectedAccountId),
      }
    : {
        okText: plaidOpen ? 'Waiting for Plaid…' : 'Continue to Plaid',
        okButtonProps: { loading: preparing || linking, disabled: plaidOpen },
        onOk: () => form.submit(),
      };

  return (
    <>
      {plaidOpen && (
        <PlaidSession
          token={session.token}
          onSuccess={onPlaidSuccess}
          onExit={onPlaidExit}
        />
      )}
      <Modal
        visible
        title="Link a bank to the treasury"
        okType="primary"
        cancelButtonProps={{ disabled: linking }}
        onCancel={onClose}
        closable={!linking}
        maskClosable={false}
        destroyOnClose
        {...okProps}
      >
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            style={{ marginBottom: 16 }}
          />
        )}
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="The new bank is born frozen"
          description="Once linked, Sila holds the bank for 5–15 minutes before it can move money. It shows up in the list right away; Fund and Withdraw through it work after that wait."
        />
        {picking ? (
          <>
            <Typography.Paragraph>
              Plaid returned several accounts at this bank. Pick the one to link
              as <Typography.Text strong>{session.accountName}</Typography.Text>
              :
            </Typography.Paragraph>
            <Radio.Group
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
            >
              <Space direction="vertical">
                {picking.accounts.map((account) => (
                  <Radio key={account.id} value={account.id}>
                    {plaidAccountLabel(account)}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </>
        ) : (
          <Form
            form={form}
            layout="vertical"
            disabled={plaidOpen || preparing}
            onFinish={prepare}
            onValuesChange={() => setError(null)}
          >
            <Form.Item
              name="accountName"
              label="Account name"
              extra="The name this bank will show under in Treasury and in the Fund / Withdraw pickers, e.g. Operating · Chase."
              rules={[
                { required: true, message: 'Name the account' },
                {
                  validator: (_, value) => {
                    const length = (value || '').trim().length;
                    if (length < 2)
                      return Promise.reject(new Error('At least 2 characters'));
                    if (length > 40)
                      return Promise.reject(new Error('At most 40 characters'));
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="e.g. Operating · Chase" autoFocus />
            </Form.Item>
            {plaidOpen && (
              <Typography.Text type="secondary">
                Plaid is open in front of this window. Close it to come back
                here.
              </Typography.Text>
            )}
          </Form>
        )}
      </Modal>
    </>
  );
}

const STATUS_COLOR = { active: 'green', inactive: 'default', frozen: 'orange' };

export function TreasuryBanksCard({ banks, loading, onDone }) {
  const client = useApolloClient();
  // { type: 'prefer' | 'retire', bank } for the confirm modal; 'link' for the Link modal.
  const [action, setAction] = useState(null);
  // Re-link in UPDATE MODE: the bank whose Link session is open, and its token.
  const [repair, setRepair] = useState(null);
  const [repairing, setRepairing] = useState(null); // accountName with a token read or a repair in flight
  const [repairBank] = useMutation(ADMIN_TREASURY_REPAIR_BANK);
  const repairAttemptRef = useRef(null);
  const repairInFlightRef = useRef(false);

  const startRepair = async (bank) => {
    if (repairInFlightRef.current) return;
    repairInFlightRef.current = true;
    setRepairing(bank.accountName);
    try {
      const { data } = await client.query({
        query: ADMIN_TREASURY_BANK_LINK_TOKEN,
        variables: { bankAccountId: bank.bankAccountId },
        fetchPolicy: 'network-only',
      });
      repairAttemptRef.current = newAttemptId();
      setRepair({
        token: data.adminTreasuryBankLinkToken,
        bank,
        attemptId: repairAttemptRef.current,
      });
    } catch (e) {
      message.error(backendMessage(e), 6);
      setRepairing(null);
    } finally {
      repairInFlightRef.current = false;
    }
  };

  const finishRepair = async () => {
    if (repairInFlightRef.current || !repair) return;
    if (repair.attemptId !== repairAttemptRef.current) return;
    repairInFlightRef.current = true;
    const { bank } = repair;
    try {
      await repairBank({ variables: { bankAccountId: bank.bankAccountId } });
      message.success(`${bankName(bank)} re-linked`);
      onDone();
    } catch (e) {
      message.error(backendMessage(e), 6);
    } finally {
      repairAttemptRef.current = null;
      repairInFlightRef.current = false;
      setRepair(null);
      setRepairing(null);
    }
  };

  const exitRepair = (err) => {
    if (err) {
      message.error(
        err.display_message ||
          err.error_message ||
          err.message ||
          'Plaid closed with an error',
        6
      );
    }
    repairAttemptRef.current = null;
    setRepair(null);
    setRepairing(null);
  };

  const columns = [
    {
      title: 'Bank',
      dataIndex: 'accountName',
      render: (accountName, bank) => (
        <>
          <Tooltip title={bank.label ? `Sila: ${accountName}` : null}>
            <TableMainLabel>{bankName(bank)}</TableMainLabel>
          </Tooltip>
          <TableSubLabel>
            {[bank.providerName || 'Bank', last4(bank), bank.accountType]
              .filter(Boolean)
              .join(' · ')}
          </TableSubLabel>
        </>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'accountStatus',
      width: 150,
      render: (status, bank) => (
        <Space size={4} wrap>
          {status && (
            <Tag color={STATUS_COLOR[status] || 'default'}>{status}</Tag>
          )}
          {bank.usable === false && (
            <Tooltip title="Sila freezes every newly linked bank for 5-15 minutes while it verifies it. It cannot move money, be retired or be preferred until that lifts.">
              <Tag color="orange">Verifying at Sila</Tag>
            </Tooltip>
          )}
          {bank.preferred && (
            <Tag color="gold" icon={<StarFilled />}>
              Preferred
            </Tag>
          )}
          {bank.connectionBroken && (
            <Tooltip title="The bank's login at Plaid expired or changed. Re-link it before moving money through it.">
              <Tag color="red" icon={<WarningOutlined />}>
                Connection broken
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 210,
      align: 'right',
      render: (_, bank) => (
        <Space size={0} wrap>
          <Button
            type="link"
            size="small"
            disabled={bank.preferred || bank.usable === false || !!repairing}
            onClick={() => setAction({ type: 'prefer', bank })}
          >
            Set as preferred
          </Button>
          {bank.canRepairConnection && (
            <Button
              type="link"
              size="small"
              icon={<SyncOutlined />}
              loading={repairing === bank.accountName}
              disabled={!!repairing && repairing !== bank.accountName}
              onClick={() => startRepair(bank)}
            >
              Re-link
            </Button>
          )}
          <Button
            type="link"
            size="small"
            danger
            disabled={bank.usable === false || !!repairing}
            onClick={() => setAction({ type: 'retire', bank })}
          >
            Retire
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Linked banks"
      loading={loading}
      style={{ height: '100%' }}
      bodyStyle={Array.isArray(banks) && banks.length ? { padding: 0 } : {}}
      extra={
        <Button
          size="small"
          type="primary"
          icon={<PlusOutlined />}
          disabled={banks == null}
          onClick={() => setAction({ type: 'link' })}
        >
          Link bank
        </Button>
      }
    >
      {banks == null ? (
        <Alert type="warning" showIcon message="Sila could not be reached" />
      ) : banks.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No bank is linked to the treasury in Sila. Fund and Withdraw stay disabled until one is."
        />
      ) : (
        <Table
          rowKey="accountName"
          columns={columns}
          dataSource={banks}
          size="small"
          pagination={false}
          showHeader={false}
        />
      )}

      {repair && (
        <PlaidSession
          token={repair.token}
          onSuccess={finishRepair}
          onExit={exitRepair}
        />
      )}
      {action && action.type === 'link' && (
        <LinkBankModal onClose={() => setAction(null)} onDone={onDone} />
      )}
      {action && action.type !== 'link' && (
        <BankActionModal
          type={action.type}
          bank={action.bank}
          onClose={() => setAction(null)}
          onDone={onDone}
        />
      )}
    </Card>
  );
}
